/**
 * ShortLinkButton - Component to copy short URL for entities
 * Usage: <ShortLinkButton entityType="stud" entityId="xxx" entityName="Lambo" />
 */
import React, { useState } from 'react';
import { Link2, Copy, Check, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

import { API_BASE as API } from '@/utils/api';

export default function ShortLinkButton({ entityType, entityId, entityName, variant = "outline", size = "sm", className = "" }) {
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [shortUrl, setShortUrl] = useState(null);

  const getShortLink = async () => {
    setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Musisz być zalogowany');
        return;
      }

      // Create custom slug from entity name (first word, max 20 chars)
      const customSlug = entityName 
        ? entityName.split(' ')[0].toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 20)
        : null;

      const response = await fetch(
        `${API}/api/panel/entities/${entityType}/${entityId}/short-url${customSlug ? `?custom_slug=${customSlug}` : ''}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Nie udało się utworzyć krótkiego linku');
      }

      const data = await response.json();
      setShortUrl(data.short_url);
      
      // Copy to clipboard
      await navigator.clipboard.writeText(data.short_url);
      setCopied(true);
      toast.success(`Skopiowano: ${data.short_url}`);
      
      // Reset copied state after 3 seconds
      setTimeout(() => setCopied(false), 3000);

    } catch (error) {
      console.error('Short link error:', error);
      toast.error('Nie udało się utworzyć linku');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      variant={variant}
      size={size}
      onClick={getShortLink}
      disabled={loading}
      className={`gap-2 ${className}`}
      data-testid={`short-link-btn-${entityType}-${entityId}`}
    >
      {loading ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : copied ? (
        <Check className="w-4 h-4 text-green-500" />
      ) : (
        <Link2 className="w-4 h-4" />
      )}
      {copied ? 'Skopiowano!' : shortUrl ? 'Kopiuj link' : 'Krótki link'}
    </Button>
  );
}
